"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type PaymentAbandonDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  confirmPendingLabel: string;
  dismissLabel: string;
  pending?: boolean;
  onConfirm: () => void;
  onDismiss?: () => void;
};

/**
 * "Leaving cancels this unpaid hold" confirmation. Shared so every checkout
 * screen asks the same question with the same destructive styling.
 */
export function PaymentAbandonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmPendingLabel,
  dismissLabel,
  pending = false,
  onConfirm,
  onDismiss,
}: PaymentAbandonDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="z-[1100] max-w-[340px] rounded-[24px] border-border bg-surface p-5 text-text-primary shadow-xl dark:border-white/[0.12] dark:bg-[#141418] dark:text-white"
        showCloseButton={false}
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-text-primary dark:text-white">
            {title}
          </DialogTitle>
          <DialogDescription className="text-text-secondary dark:text-white/58">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:flex-col-reverse">
          <Button
            type="button"
            className="h-11 rounded-xl bg-error font-semibold text-destructive-foreground hover:bg-error/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? confirmPendingLabel : confirmLabel}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-11 rounded-xl text-text-secondary hover:bg-surface-alt hover:text-text-primary dark:text-white/72 dark:hover:bg-glass-bg-08 dark:hover:text-white"
            disabled={pending}
            onClick={() => {
              onDismiss?.();
              onOpenChange(false);
            }}
          >
            {dismissLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
